import React, { useEffect, useRef, useState } from 'react';
import { countriesApi } from '../services/api';

export interface MarketOption {
  market: string;
  label: string;
  resultCount: number;
}

interface MarketPillProps {
  markets: MarketOption[];
  /** '' means no filter — "All markets". */
  selectedMarket: string;
  onSelectMarket: (market: string) => void;
}

// ISO 3166-1 alpha-2 codes map onto the Unicode "regional indicator symbol"
// block by a fixed offset, so the flag can be derived from the code itself
// with no lookup table (deepcontext-z5mx.2).
const flagEmoji = (code: string): string => {
  if (!/^[a-zA-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(...code.toUpperCase().split('').map((c) => 127397 + c.charCodeAt(0)));
};

interface MarketDisplay {
  flag: string;
  name: string;
  code?: string;
}

const displayForMarket = (market: string, countryNames: Record<string, string>): MarketDisplay => {
  // A job with no country filter (bd deepcontext-z5mx.1's 'ALL' key).
  if (market === 'ALL') return { flag: '🌍', name: 'All countries (no filter)' };
  // A bulk scan with no recorded countries anywhere (a manual Relevance
  // Engine run that predates bulk_scan_jobs.country_codes, migration 024).
  if (market === 'UNRECORDED') return { flag: '', name: 'Not recorded' };

  const codes = market.split('+');
  const names = codes.map((code) => countryNames[code] || code);
  if (codes.length === 1) {
    return { flag: flagEmoji(codes[0]), name: names[0], code: codes[0] };
  }
  return { flag: '', name: names.join(' + '), code: codes.join('+') };
};

// Caption markup identical to the Blueprint caption
// (AnalysisProfileSelect.tsx:314-318), captioned 'Market' instead.
const MarketCaption: React.FC = () => (
  <div className="flex items-center gap-[7px] pl-1.5 mb-[7px] text-[10px] font-bold uppercase leading-none tracking-[.14em] text-accent-active dark:text-accent">
    <span className="w-[5px] h-[5px] rounded-full bg-current flex-shrink-0" />
    Market
  </div>
);

/**
 * Market pill next to the Blueprint pill on Analysis Audit (bd
 * deepcontext-z5mx.2): a single market renders as a static pill (no
 * chevron); several render as 'All markets · N' opening a per-market
 * filter that narrows getHistory/getSummary to one market.
 */
const MarketPill: React.FC<MarketPillProps> = ({ markets, selectedMarket, onSelectMarket }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [countryNames, setCountryNames] = useState<Record<string, string>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Optional chaining: an older jest mock of `services/api` (predating
    // this pill) may not stub `countriesApi` at all — fall back to raw
    // country codes rather than throwing.
    countriesApi?.getAll?.()
      ?.then((countries) => {
        const map: Record<string, string> = {};
        (countries || []).forEach((c) => {
          map[c.code.toUpperCase()] = c.name;
        });
        setCountryNames(map);
      })
      ?.catch(() => { /* fall back to raw codes */ });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Hidden entirely when no blueprint is selected (empty markets list).
  if (markets.length === 0) return null;

  // Case 1: results come from one market — static pill, no chevron.
  if (markets.length === 1) {
    const { flag, name, code } = displayForMarket(markets[0].market, countryNames);
    return (
      <div className="w-fit flex-shrink-0">
        <MarketCaption />
        <div
          data-testid="market-pill"
          className="h-[42px] border border-line rounded-full bg-surface flex items-center gap-2.5 px-4 text-sm font-bold text-ink whitespace-nowrap"
        >
          {flag && <span>{flag}</span>}
          <span>{name}</span>
          {code && <span className="text-ink-muted font-semibold">{code}</span>}
        </div>
      </div>
    );
  }

  // Case 2: results mix several markets — 'All markets · N' with a chevron
  // opening a list with per-market counts. N on the closed trigger is the
  // number of distinct markets (the aggregate result count only appears on
  // the panel's own 'All markets' row) per the locked design.
  const totalCount = markets.reduce((sum, m) => sum + m.resultCount, 0);
  const selectedEntry = selectedMarket ? markets.find((m) => m.market === selectedMarket) : undefined;
  const triggerLabel = selectedEntry ? displayForMarket(selectedEntry.market, countryNames).name : 'All markets';
  const triggerCount = selectedEntry ? selectedEntry.resultCount : markets.length;

  return (
    <div ref={dropdownRef} className="relative w-fit flex-shrink-0">
      <MarketCaption />
      <div
        data-testid="market-pill"
        role="button"
        className="h-[42px] w-[224px] border-2 border-accent rounded-full bg-surface flex items-center justify-between gap-2.5 px-3.5 text-sm font-bold text-ink cursor-pointer"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="truncate flex items-baseline gap-1.5">
          <span className="truncate">{triggerLabel}</span>
          <span className="text-ink-muted font-semibold">{triggerCount.toLocaleString()}</span>
        </span>
        <svg
          className={`w-3 h-3 text-accent flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {isOpen && (
        <div
          data-testid="market-pill-panel"
          className="absolute mt-2 min-w-[260px] w-max bg-surface border border-line rounded-xl shadow-lg p-1.5 z-20"
        >
          <button
            type="button"
            data-testid="market-option-all"
            onClick={() => { onSelectMarket(''); setIsOpen(false); }}
            className={`w-full flex items-center justify-between gap-6 px-3 py-2 rounded-lg text-sm text-left ${
              !selectedMarket ? 'bg-nav-active text-accent-active font-bold' : 'text-ink'
            }`}
          >
            <span>All markets</span>
            <span className="text-ink-muted font-medium">{totalCount.toLocaleString()}</span>
          </button>
          {markets.map((m) => {
            const { flag, name, code } = displayForMarket(m.market, countryNames);
            const isSelected = selectedMarket === m.market;
            return (
              <button
                type="button"
                key={m.market}
                data-testid={`market-option-${m.market}`}
                onClick={() => { onSelectMarket(m.market); setIsOpen(false); }}
                className={`w-full flex items-center justify-between gap-6 px-3 py-2 rounded-lg text-sm text-left ${
                  isSelected ? 'bg-nav-active text-accent-active font-bold' : 'text-ink'
                }`}
              >
                <span>{flag ? `${flag} ` : ''}{name}{code ? ` · ${code}` : ''}</span>
                <span className="text-ink-muted font-medium">{m.resultCount.toLocaleString()}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MarketPill;
